const FORMAT_TAGS = { bold: 'b', italic: 'i' };
const ZERO_WIDTH_SPACE = '\u200B';

import { initMentionAutocomplete } from './mention_autocomplete';

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function plainTextToHtml(text) {
  if (!text) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) return normalizeEditorHtml(text);
  return escapeHtml(text).replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
}

function normalizeEditorHtml(html) {
  if (!html || !html.trim()) return '';

  const el = document.createElement('div');
  el.innerHTML = html;

  const blockTags = ['div', 'p'];
  let changed = true;

  while (changed) {
    changed = false;
    el.querySelectorAll(blockTags.join(',')).forEach((block) => {
      const hasNestedBlock = block.querySelector('div, p');
      const text = block.textContent.replace(/[\u00a0\u200B]/g, ' ').trim();

      if (!text && !block.querySelector('img, br')) {
        block.remove();
        changed = true;
        return;
      }

      if (!hasNestedBlock) {
        const fragment = document.createDocumentFragment();
        while (block.firstChild) {
          fragment.appendChild(block.firstChild);
        }
        block.replaceWith(fragment);
        changed = true;
      }
    });
  }

  return el.innerHTML
    .replace(/[\u200B\uFEFF]/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
    .replace(/<b>\s*<\/b>/gi, '')
    .replace(/<i>\s*<\/i>/gi, '')
    .trim();
}

function editorPlainText(html) {
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent || '').replace(/[\u00a0\u200B]/g, ' ').trim();
}

function configureEditorDocument() {
  try {
    document.execCommand('styleWithCSS', false, false);
    document.execCommand('defaultParagraphSeparator', false, 'br');
  } catch (_e) {
    // Unsupported in some browsers
  }
}

function saveSelection(content) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  if (!content.contains(range.commonAncestorContainer)) return;

  content._savedRange = range.cloneRange();
}

function restoreSelection(content) {
  if (!content._savedRange) return;

  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(content._savedRange);
}

function ensureEditorRange(content) {
  const selection = window.getSelection();
  let range = null;

  if (selection && selection.rangeCount > 0) {
    range = selection.getRangeAt(0);
    if (!content.contains(range.commonAncestorContainer)) {
      range = null;
    }
  }

  if (!range) {
    range = document.createRange();
    if (content.lastChild) {
      range.selectNodeContents(content);
      range.collapse(false);
    } else {
      range.setStart(content, 0);
      range.collapse(true);
    }
    selection.removeAllRanges();
    selection.addRange(range);
  }

  return range;
}

function wrapRangeWithTag(range, tagName) {
  const wrapper = document.createElement(tagName);

  try {
    range.surroundContents(wrapper);
  } catch (_e) {
    wrapper.appendChild(range.extractContents());
    range.deleteContents();
    range.insertNode(wrapper);
  }

  const selection = window.getSelection();
  const newRange = document.createRange();
  newRange.selectNodeContents(wrapper);
  newRange.collapse(false);
  selection.removeAllRanges();
  selection.addRange(newRange);

  return wrapper;
}

function insertInlineFormat(tagName, range) {
  const wrapper = document.createElement(tagName);
  wrapper.appendChild(document.createTextNode(ZERO_WIDTH_SPACE));
  range.insertNode(wrapper);

  const selection = window.getSelection();
  const newRange = document.createRange();
  newRange.setStart(wrapper.firstChild, 1);
  newRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(newRange);

  return wrapper;
}

function applyFormatting(command, content) {
  content.focus();
  configureEditorDocument();

  const range = ensureEditorRange(content);
  const tagName = FORMAT_TAGS[command];

  if (document.execCommand(command, false, null)) {
    return;
  }

  if (!tagName) return;

  if (range.collapsed) {
    insertInlineFormat(tagName, range);
  } else {
    wrapRangeWithTag(range, tagName);
  }
}

function updateToolbarState(editor) {
  const content = editor.querySelector('[data-rich-text-content]');
  editor.querySelectorAll('[data-command]').forEach((btn) => {
    const command = btn.dataset.command;
    let active = false;
    try {
      active = document.queryCommandState(command);
    } catch (_e) {
      active = false;
    }
    btn.classList.toggle('is-active', active);
  });
  if (!content) return;
  content.classList.toggle('is-empty', editorPlainText(content.innerHTML).length === 0);
}

function syncRichTextEditor(editor, normalize = false) {
  const content = editor.querySelector('[data-rich-text-content]');
  const input = editor.querySelector('[data-rich-text-input]');
  if (!content || !input) return;

  const html = content.innerHTML;
  input.value = normalize ? normalizeEditorHtml(html) : html;
}

window.syncRichTextEditors = function syncRichTextEditors(root = document, normalize = false) {
  root.querySelectorAll('[data-rich-text-editor]').forEach((editor) => {
    syncRichTextEditor(editor, normalize);
  });
};

window.initRichTextEditors = function initRichTextEditors(root = document) {
  root.querySelectorAll('[data-rich-text-editor]').forEach((editor) => {
    if (editor.dataset.richTextInitialized === 'true') return;
    editor.dataset.richTextInitialized = 'true';

    const content = editor.querySelector('[data-rich-text-content]');
    const input = editor.querySelector('[data-rich-text-input]');
    const form = editor.closest('form');
    if (!content || !input) return;

    const rows = parseInt(content.dataset.rows || '8', 10);
    content.style.minHeight = `${Math.max(rows * 22, 120)}px`;

    content.innerHTML = plainTextToHtml(input.value);

    content.addEventListener('focus', configureEditorDocument);

    content.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        if (editor.querySelector('[data-mention-dropdown]')) return;

        event.preventDefault();
        configureEditorDocument();
        document.execCommand('insertLineBreak');
      }
    });

    editor.querySelectorAll('[data-command]').forEach((btn) => {
      btn.addEventListener('mousedown', (event) => {
        event.preventDefault();
        saveSelection(content);
      });

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        restoreSelection(content);
        applyFormatting(btn.dataset.command, content);
        syncRichTextEditor(editor, false);
        updateToolbarState(editor);
      });
    });

    const onChange = () => {
      syncRichTextEditor(editor, false);
      updateToolbarState(editor);
    };

    content.addEventListener('input', onChange);
    content.addEventListener('keyup', onChange);
    content.addEventListener('mouseup', onChange);

    initMentionAutocomplete(editor, content, input);

    if (form) {
      form.addEventListener('submit', () => {
        syncRichTextEditor(editor, true);
        if (editor.classList.contains('rich-text-editor--required') && !input.value.replace(/<[^>]+>/g, '').trim()) {
          content.focus();
        }
      });
    }

    updateToolbarState(editor);
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initRichTextEditors();
});

$(document).on('ajaxComplete', () => {
  const modal = document.getElementById('default_modal');
  initRichTextEditors(modal || document);
});
